<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import { computed, type HTMLAttributes, useAttrs } from 'vue'
import { cn } from '../../../lib/utils'

/**
 * shadcn-vue `Input`, in the brand control voice (BG §22.4, BG §19.3): the same mono face, system tracking and
 * 3.25rem height as `ZhButton`, so a field and the command beside it read as siblings. Colour and radius
 * still come from the semantic layer in theme.css (`--input`, `--ring`, `--radius-*`). The placeholder is
 * the uppercase affordance; what someone types keeps its own case. No outline suppression: the
 * `@layer base` `:focus-visible` rule draws the cyan ring (BG §22.3).
 *
 * `type="file"` renders without any value binding: a browser only lets a file input's `value` be written as the
 * empty string, so a text model would write the chosen file's fake path back on the next render and throw
 * `InvalidStateError` (OS-PORTAL-4). A file `Input` emits no `update:modelValue`; read the pick from `@change`.
 */
const props = defineProps<{
    defaultValue?: string | number
    modelValue?: string | number
    class?: HTMLAttributes['class']
}>()

const emits = defineEmits<{
    (e: 'update:modelValue', payload: string | number): void
}>()

const modelValue = useVModel(props, 'modelValue', emits, {
    passive: true,
    defaultValue: props.defaultValue,
})

const attrs = useAttrs()
const isFile = computed(() => String(attrs.type ?? '').toLowerCase() === 'file')

const classes = computed(() =>
    cn(
        'dark:bg-input/30 border-input focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 disabled:bg-input/50 dark:disabled:bg-input/80 h-13 rounded-lg border bg-transparent px-4 py-1 font-mono tracking-[var(--tracking-system)] text-base transition-colors file:h-6 file:text-sm file:font-medium file:uppercase focus-visible:ring-3 aria-invalid:ring-3 md:text-sm w-full min-w-0 file:inline-flex file:border-0 file:bg-transparent file:text-foreground placeholder:text-muted-foreground placeholder:uppercase disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
        props.class,
    ),
)
</script>

<template>
  <input
    v-if="isFile"
    data-slot="input"
    :class="classes"
  >
  <input
    v-else
    v-model="modelValue"
    data-slot="input"
    :class="classes"
  >
</template>
